import { useEffect, useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { fileBlob, listFiles, type OemFile } from '../../lib/oem-client';
import { runtimeMode } from '../../lib/auth';
import { MediaDialog } from './media-dialog';
interface LoadedMedia {
  file: OemFile;
  url: string;
}
// URLs are ephemeral protected-response blobs, never persisted or service-worker cached.
export function RecordGallery({
  projectId,
  recordIds,
  label,
  className = 'sample-photo',
}: {
  readonly projectId: string;
  readonly recordIds: readonly string[];
  readonly label: string;
  readonly className?: string;
}) {
  const [media, setMedia] = useState<LoadedMedia[]>([]),
    [selected, setSelected] = useState(0),
    [zoom, setZoom] = useState(false),
    [error, setError] = useState('');
  const identities = recordIds.join(',');
  useEffect(() => {
    if (runtimeMode !== 'connected') return;
    let active = true;
    const urls: string[] = [];
    void (async () => {
      const registered = (await listFiles(projectId)).filter(
        (f) =>
          identities.split(',').includes(f.recordId) &&
          ['image/png', 'image/jpeg'].includes(f.mime),
      );
      const loaded: LoadedMedia[] = [];
      for (const file of registered) {
        const blob = await fileBlob(file.id);
        const hash = Array.from(
          new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())),
        )
          .map((x) => x.toString(16).padStart(2, '0'))
          .join('');
        if (hash !== file.sha256) throw Error('file_hash_mismatch');
        const url = URL.createObjectURL(blob);
        urls.push(url);
        loaded.push({ file, url });
      }
      if (active) setMedia(loaded);
      else urls.forEach((url) => URL.revokeObjectURL(url));
    })().catch(() => {
      if (active) setError('ไม่สามารถยืนยันไฟล์ในขอบเขตนี้ได้');
    });
    return () => {
      active = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [projectId, identities]);
  const current = media[selected];
  const url = current?.url ?? '/concepts/cream-packaging-concept-800.webp';
  const caption = current
    ? `${label} · ${current.file.state} · SHA-256 ${current.file.sha256}`
    : runtimeMode === 'fixture'
      ? 'AI concept · ภาพประกอบ ไม่ใช่สินค้าหรือตัวอย่างจริง'
      : 'ยังไม่มีภาพที่อ่านได้ในเวอร์ชันนี้ · ภาพแนวคิด AI เป็นภาพประกอบ';
  return (
    <>
      <figure className={className}>
        <button
          className="image-button"
          aria-label={`ขยาย ${label}${current ? ' exact file' : ' ภาพแนวคิด'}`}
          onClick={() => setZoom(true)}
        >
          <img
            src={url}
            srcSet={
              current
                ? undefined
                : '/concepts/cream-packaging-concept-400.webp 400w, /concepts/cream-packaging-concept-800.webp 800w, /concepts/cream-packaging-concept-1200.webp 1200w'
            }
            sizes="(max-width: 768px) 100vw, 700px"
            loading="lazy"
            decoding="async"
            alt={
              current
                ? `หลักฐานไฟล์ ${label} ${current.file.id}`
                : 'ภาพแนวคิด AI ไม่ใช่ภาพสินค้าหรือตัวอย่างจริง'
            }
            width="1536"
            height="1024"
          />
          <ZoomIn />
        </button>
        <figcaption className="sample-caption wrap">{caption}</figcaption>
      </figure>
      {media.length > 1 ? (
        <div className="thumbnails">
          {media.map((item, i) => (
            <button
              key={item.file.id}
              className="thumbnail"
              aria-label={`ไฟล์ ${i + 1} · exact file ${item.file.id}`}
              aria-pressed={i === selected}
              onClick={() => setSelected(i)}
            >
              ไฟล์ {i + 1} · {item.file.state}
            </button>
          ))}
        </div>
      ) : null}
      {error ? (
        <p className="note-warning" role="status">
          {error}
        </p>
      ) : null}
      {zoom ? (
        <MediaDialog
          label={`${label} · ${current ? 'exact file' : 'ภาพแนวคิด'}`}
          onDismiss={() => setZoom(false)}
        >
          <button className="action secondary" onClick={() => setZoom(false)}>
            ปิดภาพขยาย
          </button>
          <img
            src={current?.url ?? '/concepts/cream-packaging-concept.png'}
            alt={
              current
                ? `หลักฐาน exact file ${current.file.id}`
                : 'ภาพแนวคิด AI ไม่ใช่หลักฐานผลิตภัณฑ์'
            }
          />
          <p className="wrap">{caption}</p>
          {current ? (
            <p className="version">
              Record {current.file.recordId} · File {current.file.id}
            </p>
          ) : null}
        </MediaDialog>
      ) : null}
    </>
  );
}
